import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Send, SkipForward, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { CHANGELOG, LATEST_VERSION } from '../lib/changelog';

const SEEN_KEY = 'olta-last-seen-version';

// Release notes: auto-popup DISABLED (user request) — access stays via the sidebar
// version button (admins), which also hosts the push-to-team decision.
export function WhatsNewModal() {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [releases, setReleases] = useState([]);
  const [needsDecision, setNeedsDecision] = useState(false);
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    // mark as seen so dependent modals (Monday digest) are never blocked
    try { localStorage.setItem(SEEN_KEY, LATEST_VERSION); } catch (e) { /* ignore */ }
    if (!isAdmin) return;
    api.whatsnewStatus().then(res => {
      const status = res.data || {};
      setNeedsDecision(!(status.decided && status.decided[LATEST_VERSION]));
    }).catch(() => { /* not logged in / offline */ });
  }, [isAdmin]);

  useEffect(() => {
    const handler = () => { setReleases(CHANGELOG); setOpen(true); };
    window.addEventListener('open-whats-new', handler);
    return () => window.removeEventListener('open-whats-new', handler);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(SEEN_KEY, LATEST_VERSION);
    } catch (e) { /* ignore */ }
    setOpen(false);
    window.dispatchEvent(new Event('whats-new-dismissed'));
  };

  const decide = async (decision) => {
    setDeciding(true);
    try {
      await api.decideWhatsnew(LATEST_VERSION, decision);
      toast.success(decision === 'approved'
        ? `v${LATEST_VERSION} will now be shown once to all team members & AC`
        : `Push skipped — v${LATEST_VERSION} stays admin-only`);
      setNeedsDecision(false);
      dismiss();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save the decision'); }
    setDeciding(false);
  };

  if (releases.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="whats-new-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-400" /> What's New
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          {releases.map((r) => (
            <div key={r.version} data-testid={`changelog-${r.version}`}>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-sm font-mono text-blue-400">v{r.version}</span>
                <span className="text-[11px] text-muted-foreground">{r.date}</span>
              </div>
              <ul className="space-y-2.5">
                {r.changes.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-xs text-muted-foreground leading-relaxed">
                    <span className="shrink-0 text-sm leading-none mt-0.5">{c.emoji}</span>
                    <span>{c.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {isAdmin && needsDecision ? (
          <div className="pt-2 space-y-2">
            <p className="text-[11px] text-muted-foreground">Push v{LATEST_VERSION} to all other members (team + AC)? They will see it once on their next visit.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => decide('skipped')} disabled={deciding} data-testid="whats-new-skip-push"
                className="text-muted-foreground rounded-sm text-xs h-9 px-3">
                <SkipForward className="w-3.5 h-3.5 mr-1.5" /> Skip push
              </Button>
              <Button onClick={() => decide('approved')} disabled={deciding} data-testid="whats-new-approve-push"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-9 px-4">
                {deciding ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Push to team & AC
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end pt-2">
            <Button onClick={dismiss} data-testid="whats-new-dismiss"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-9 px-4">Got it</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
